// src/components/ScraperSettingsFields.jsx
import React from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  Select,
  MenuItem,
  Grid,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Collapse,
  Chip,
  Divider,
  Switch,
  FormControlLabel,
  Stack,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import HelpTooltip from './HelpTooltip';
import MarketSelector from './MarketSelector';
import DisabledReason from './common/DisabledReason';
import { SHADOWS } from '../theme/tokens';

const API = process.env.REACT_APP_API_BASE_URL || '';

/**
 * Die Techniken, die `backend/scrapers/pipeline.py` (`DEFAULT_TECHNIQUES`) kennt,
 * in derselben Reihenfolge, plus je einen Satz, der sagt WANN man sie nimmt.
 *
 * 🔴 Bis E3a-2 war das hier ein **Schalter**: `forceSelenium = technique ===
 * 'selenium'`, und im Lesemodus stand für alles andere „Smart pipeline (HTML
 * first)". Der Wert `nextjs` liess sich damit weder anzeigen noch setzen — die
 * Anzeige hat ihn sogar falsch benannt, und ein Klick auf den Schalter hätte ihn
 * durch `html` ersetzt.
 *
 * Das war folgenlos, solange nichts `nextjs` vorschlagen konnte. Seit E3a-1 kann
 * es der Generator, und die Technik ist genau die, die den ARCO-Fehlpreis gelöst
 * hat. Ein Vorschlag, den die Oberfläche nicht zeigen kann, ist kein Vorschlag.
 *
 * Reihenfolge = Pipeline-Reihenfolge, damit „welche zuerst" ablesbar bleibt.
 * `perplexity` steht bewusst NICHT zur Wahl: es liest die Seite nicht, sondern
 * antwortet über sie, und bleibt der Notnagel am Ende der Pipeline.
 */
const TECHNIQUE_OPTIONS = [
  { value: 'jsonld', label: 'JSON-LD', hint: 'Structured product data in a <script> tag. Risky when several Product blocks exist (carousel).' },
  { value: 'html', label: 'HTML (default)', hint: 'CSS selectors on ordinary markup. The right answer for most shops.' },
  { value: 'xpath', label: 'XPath', hint: 'When no CSS selector can express the position.' },
  { value: 'nextjs', label: 'Next.js (__NEXT_DATA__)', hint: 'Shop renders with Next.js. Immune to carousel confusion.' },
  { value: 'selenium', label: 'Selenium (real browser)', hint: 'Price only appears after JavaScript runs. Costly.' },
  { value: 'oembed', label: 'oEmbed', hint: 'Rare. Shop exposes an oEmbed endpoint carrying the price.' },
];
const SECTION_PAPER_SX = {
  p: 3,
  borderRadius: 3,
  borderColor: 'divider',
  boxShadow: SHADOWS.panel,
  bgcolor: 'background.paper',
};

const STOCK_METHODS = [
  { value: 'disabled_attribute', label: 'Element has disabled attribute' },
  { value: 'text_contains',      label: 'Element contains "Out of Stock" text' },
  { value: 'element_in_stock',   label: 'Element contains "In Stock" text' },
  { value: 'element_exists',     label: 'Out-of-stock element exists' },
];

// --- Helper Components ---

const hasValue = (value) => value !== undefined && value !== null && value !== '';

const SelectorInput = ({ label, values = [], onChange, helpText, snippet }) => {
    const [inputValue, setInputValue] = React.useState('');
    const handleAdd = () => {
        if (inputValue && !values.includes(inputValue)) {
            onChange([...values, inputValue]);
        }
        setInputValue('');
    };
    const handleDelete = (valueToDelete) => {
        onChange(values.filter((value) => value !== valueToDelete));
    };

    return (
        <Box>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
              {label}
              <HelpTooltip title={helpText} snippet={snippet} />
            </Typography>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <List dense disablePadding>
                    {(!values || values.length === 0) && (
                        <ListItem>
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', p: 1 }}>
                                No selectors added.
                            </Typography>
                        </ListItem>
                    )}
                    {values.map((value, index) => (
                        <ListItem key={index} divider
                            secondaryAction={
                                <IconButton edge="end" size="small" onClick={() => handleDelete(value)}
                                  aria-label={`Delete selector ${value}`}><Delete fontSize="small" /></IconButton>
                            }
                            sx={{ pl: 1, pr: '40px' }}
                        >
                            <ListItemText primary={value} primaryTypographyProps={{ style: { whiteSpace: 'normal', wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.8rem' }}}/>
                        </ListItem>
                    ))}
                    <ListItem sx={{ backgroundColor: 'action.hover' }}>
                        <TextField
                            placeholder="Enter a new selector…"
                            fullWidth
                            variant="standard"
                            size="small"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); }}}
                            InputProps={{
                                disableUnderline: true,
                                endAdornment: (<Button onClick={handleAdd} size="small" >Add</Button>),
                            }}
                        />
                    </ListItem>
                </List>
            </Box>
        </Box>
    );
};

// NEW: A component to display fields in "view" mode
const ViewField = ({ label, value }) => {
    const isEmpty = !value && value !== 0 && value !== false && (!Array.isArray(value) || value.length === 0);
    const displayValue = Array.isArray(value) ? value.join(', ') : value;

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5, color: 'text.secondary' }}>
                {label}
            </Typography>
            {isEmpty ? (
                <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.disabled' }}>
                    Not configured
                </Typography>
            ) : (
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>
                    {String(displayValue)}
                </Typography>
            )}
        </Box>
    );
};


/**
 * ScraperSettingsFields — die Scrape-Einstellungen eines Wettbewerbers oder Ziels.
 *
 * @description
 *   Ein Feldblock, kein Formular: Technikwahl, Preis-/Bestands-/Lieferzeit-Selektoren,
 *   XPath-Rückfall, Verfügbarkeitsstrategien, MwSt. und (optional) Versandregeln.
 *   Zustand und Speichern gehören dem Aufrufer; diese Komponente rendert nur und
 *   meldet Änderungen nach oben. Sie wird von ZWEI Bildschirmen benutzt, die
 *   verschiedene Teilmengen laden — deshalb `show*`-Schalter statt zweier Kopien.
 *
 * @useWhen
 *   - Scrape-Konfiguration eines Wettbewerbers bearbeiten oder anzeigen
 *   - Scrape-Konfiguration eines einzelnen Ziels bearbeiten (Override)
 *   - Ein Bildschirm braucht dieselben Selektorfelder in Lese- UND Schreibform
 *
 * @dontUseFor
 *   - Ein einzelnes Selektorfeld — dafür direkt `TextField` mit `HelpTooltip`
 *   - Discovery-Kandidaten: die tragen keine Scrape-Konfiguration
 *
 * @related
 *   - MarketSelector — für die Marktzuordnung, hier eingebettet
 *   - HelpTooltip — die Erklärungen an den Feldern
 *
 * @reuseHints
 *   - Die Vorschau-Oberfläche aus E4 („Vorschlag neben Ist-Konfiguration") wird
 *     dieselben Felder zweimal nebeneinander brauchen — dann als zwei Instanzen
 *     im Lesemodus, nicht als dritte Kopie der Feldliste.
 *
 * @props
 *   technique            Aktuelle `defaultTechnique`; `''`/undefined → `html`.
 *   onTechniqueChange    Fehlt sie, wird die Technik nur angezeigt, nicht editierbar.
 *   config               Die Konfiguration; gelesene Schlüssel siehe Destrukturierung unten.
 *   onChange             (feld, wert) für jedes Feld ausser Versandregeln.
 *   onShippingRulesChange  Eigener Kanal für die Regelliste; fehlt er, geht es über onChange.
 *   editMode             Schaltet den GESAMTEN Block zwischen Lesen und Bearbeiten.
 *   showShippingRules      Nur der Wettbewerber-Bildschirm hat Versandregeln.
 *   showDeliveryTimeSelector  Dito für die Lieferzeit.
 *
 * 🔴 **Die Technikwahl war bis 2026-08-14 ein Schalter** („Force Selenium"), der
 * genau zwei Werte erzeugen konnte und im Lesemodus alles Nicht-Selenium als
 * „Smart pipeline (HTML first)" bezeichnete. Das war folgenlos, solange niemand
 * eine dritte Technik setzte — seit E3a schlägt der KI-Generator `nextjs` vor,
 * und ein Vorschlag, den die Oberfläche weder zeigen noch halten kann, ist kein
 * Vorschlag. Ein unbekannter Wert bekommt jetzt einen eigenen, markierten
 * Eintrag, statt als leeres Feld zu erscheinen und beim Speichern ersetzt zu
 * werden.
 */
export default function ScraperSettingsFields({
  technique,
  onTechniqueChange,
  config,
  onChange,
  onShippingRulesChange,
  editMode, // This prop now controls the entire component's state
  showShippingRules = false,
  showDeliveryTimeSelector = false,
}) {
  const {
    priceSelector,
    priceAttr,
    salePriceSelector,
    salePriceAttr,
    saleIndicatorSelector,
    saleIndicatorTextSelector,
    saleIndicatorText,
    stockMethod,
    stockSelector,
    stockText,
    shippingRules = [],
    shippingErrors = {},
    deliveryTimeSelector,
    buyButtonSelectors,
    xpathPriceSelector,
    xpathPriceAttr,
    xpathSalePriceSelector,
    xpathSalePriceAttr,
    xpathAvailabilitySelector,
    xpathAvailabilityText,
    applyVat,
    vatRate,
    availabilityStrategies = [],
  } = config || {};

  const handleShippingRulesChange = onShippingRulesChange || (r => onChange('shippingRules', r));
  const effectiveTechnique = (technique || 'html').toLowerCase();
  const knownTechnique = TECHNIQUE_OPTIONS.some(o => o.value === effectiveTechnique);
  const techniqueLabel = (value) =>
    TECHNIQUE_OPTIONS.find(o => o.value === value)?.label || value;
  const handleAvailabilityStrategiesChange = (next) => onChange('availabilityStrategies', next);

  const addAvailabilityStrategy = () => {
    const next = [...(availabilityStrategies || []), { method: 'text_contains', selector: '', text: [] }];
    handleAvailabilityStrategiesChange(next);
  };

  const updateAvailabilityStrategy = (idx, key, value) => {
    const next = [...(availabilityStrategies || [])];
    next[idx] = { ...(next[idx] || {}), [key]: value };
    handleAvailabilityStrategiesChange(next);
  };

  const removeAvailabilityStrategy = (idx) => {
    const next = [...(availabilityStrategies || [])];
    next.splice(idx, 1);
    handleAvailabilityStrategiesChange(next);
  };

  // NEW: load markets so we can show display names and store canonical ids
  const [markets, setMarkets] = React.useState([]);
  React.useEffect(() => {
    fetch(`${API}/api/markets`).then(r => r.json()).then(setMarkets).catch(() => {});
  }, []);
  const marketsById = React.useMemo(() => Object.fromEntries(markets.map(m => [m.id, m])), [markets]);
  const toOption = React.useCallback((id) => {
    if (!id) return null;
    const m = marketsById[id];
    return m ? { id: m.id, label: `${m.name} (${m.code})` } : { id, label: id };
  }, [marketsById]);

  return (
    <Box display="flex" flexDirection="column" gap={3}>
        <Paper variant="outlined" sx={SECTION_PAPER_SX}>
            <Typography variant="h6" gutterBottom>Scrape Engine & VAT</Typography>
            {editMode && typeof onTechniqueChange === 'function' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControl size="small" fullWidth>
                        <InputLabel id="default-technique-label">Technique to try first</InputLabel>
                        <Select
                            labelId="default-technique-label"
                            label="Technique to try first"
                            // 🔴 IMMER `effectiveTechnique`, nie `''`.
                            //
                            // Die erste Fassung stand hier auf
                            // `knownTechnique ? effectiveTechnique : ''` — und machte damit
                            // genau den Fall kaputt, für den der markierte Eintrag unten
                            // gebaut ist: der Eintrag existierte, war aber nie ausgewählt,
                            // und das Feld blieb LEER. Kommentar, JSDoc-Kopf und Changelog
                            // behaupteten alle drei das Gegenteil.
                            //
                            // Gefunden hat es das Regel-15-Gate am gerenderten DOM, nicht
                            // die Durchsicht — und auch keiner der sechs Playwright-Tests:
                            // die fahren alle `nextjs`, also einen BEKANNTEN Wert, und
                            // konnten den Zweig strukturell nicht erreichen. Deshalb G.7.
                            //
                            // Der Wert findet immer einen passenden `MenuItem`: die sechs
                            // bekannten, plus den unten, der genau dann gerendert wird,
                            // wenn keiner der sechs passt.
                            value={effectiveTechnique}
                            onChange={(e) => onTechniqueChange(e.target.value)}
                            renderValue={(v) => techniqueLabel(v)}
                            aria-describedby="default-technique-helper"
                        >
                            {TECHNIQUE_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    <Box>
                                        <Typography variant="body2">{opt.label}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {opt.hint}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                            {/* Ein unbekannter Wert bekommt einen eigenen Eintrag, statt als
                                leeres Feld zu erscheinen. Sonst sähe „Wert, den diese Oberfläche
                                nicht kennt" genauso aus wie „nichts gesetzt" — und ein Speichern
                                würde ihn wortlos ersetzen. */}
                            {!knownTechnique && effectiveTechnique && (
                                <MenuItem value={effectiveTechnique}>
                                    <Box>
                                        <Typography variant="body2">{effectiveTechnique}</Typography>
                                        <Typography variant="caption" color="warning.main">
                                            Unknown technique — kept as-is unless you change it
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            )}
                        </Select>
                        {/* `FormHelperText` statt eines freistehenden `Typography`:
                            MUI verdrahtet es ueber `aria-describedby` mit dem Auswahlfeld,
                            damit ein Screenreader den Hinweis beim Feld vorliest statt als
                            losen Text daneben. */}
                        <FormHelperText id="default-technique-helper">
                            Ordering only — every other technique still runs as a fallback.
                        </FormHelperText>
                    </FormControl>
                    <Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    color="primary"
                                    checked={Boolean(applyVat)}
                                    onChange={(e) => onChange('applyVat', e.target.checked)}
                                />
                            }
                            label="Add VAT to net prices"
                        />
                        <TextField
                            label="VAT rate (%)"
                            type="number"
                            size="small"
                            sx={{ mt: 1, maxWidth: 200 }}
                            value={hasValue(vatRate) ? vatRate : ''}
                            onChange={(e) => onChange('vatRate', e.target.value)}
                            InputProps={{ inputProps: { min: 0, step: 0.1 } }}
                            disabled={!applyVat}
                            helperText="Automatically gross net prices by this percentage."
                        />
                    </Box>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {/* 🔴 Die Anzeige nennt die Technik beim Namen.
                        Vorher stand hier für ALLES ausser `selenium` „Smart pipeline
                        (HTML first)" — bei einem `nextjs`- oder `jsonld`-Ziel war das
                        schlicht falsch, und niemand konnte es sehen. */}
                    <ViewField
                        label="Technique to try first"
                        value={techniqueLabel(effectiveTechnique)
                               + (knownTechnique ? '' : ' (unknown)')}
                    />
                    <ViewField
                        label="VAT handling"
                        value={applyVat ? `Enabled (${hasValue(vatRate) ? vatRate : '?'}%)` : 'Disabled'}
                    />
                </Box>
            )}
        </Paper>
        <Paper variant="outlined" sx={SECTION_PAPER_SX}>
            <Typography variant="h6" gutterBottom>Price & Sale Detection</Typography>
            
            <Box sx={{ display: 'flex', gap: 4, mt: 2 }}>
                {/* --- Column 1: Regular Price --- */}
                <Box sx={{ width: '50%' }}>
                    {editMode ? (
                        <>
                            <SelectorInput
                                label="Regular Price Selectors"
                                values={priceSelector ? priceSelector.split(',').map(s => s.trim()) : []}
                                onChange={(values) => onChange('priceSelector', values.join(','))}
                                helpText="CSS selectors for the regular price. Tries them in order."
                                snippet="e.g., .price-tag > span"
                            />
                            <Box sx={{ mt: 2 }}>
                                <SelectorInput
                                    label="Regular Price Attribute"
                                    values={priceAttr ? priceAttr.split(',').map(s => s.trim()) : []}
                                    onChange={(values) => onChange('priceAttr', values.join(','))}
                                    helpText="Attribute containing the price (e.g., data-price)."
                                    snippet="e.g., data-price"
                                />
                            </Box>
                        </>
                    ) : (
                        <>
                            <ViewField label="Regular Price Selectors" value={priceSelector} />
                            <ViewField label="Regular Price Attribute" value={priceAttr} />
                        </>
                    )}
                </Box>

                {/* --- Column 2: Sale Price --- */}
                <Box sx={{ width: '50%' }}>
                    {editMode ? (
                        <>
                            <SelectorInput
                                label="Sale Price Selectors"
                                values={salePriceSelector ? salePriceSelector.split(',').map(s => s.trim()) : []}
                                onChange={(values) => onChange('salePriceSelector', values.join(','))}
                                helpText="Selectors for the element containing ONLY the sale price."
                                snippet="e.g., .sale-price"
                            />
                            <Box sx={{ mt: 2 }}>
                                <SelectorInput
                                    label="Sale Price Attribute"
                                    values={salePriceAttr ? salePriceAttr.split(',').map(s => s.trim()) : []}
                                    onChange={(values) => onChange('salePriceAttr', values.join(','))}
                                    helpText="Attribute containing the sale price."
                                    snippet="e.g., data-sale-price"
                                />
                            </Box>
                        </>
                    ) : (
                        <>
                            <ViewField label="Sale Price Selectors" value={salePriceSelector} />
                            <ViewField label="Sale Price Attribute" value={salePriceAttr} />
                        </>
                    )}
                </Box>
            </Box>
            
            <Divider sx={{ my: 3 }}><Chip label="Secondary Sale Checks" size="small" /></Divider>

            <Box sx={{ display: 'flex', gap: 4 }}>
                 {/* --- Column 1: Indicator Element --- */}
                <Box sx={{ width: '50%' }}>
                    {editMode ? (
                        <SelectorInput
                            label="Indicator Element Selectors"
                            values={saleIndicatorSelector ? saleIndicatorSelector.split(',').map(s => s.trim()) : []}
                            onChange={(values) => onChange('saleIndicatorSelector', values.join(','))}
                            helpText="Selectors for a visual badge or banner (e.g., a 'SALE!' icon or a crossed-out price)."
                            snippet="e.g. .sale-badge, .price del"
                        />
                    ) : (
                        <ViewField label="Indicator Element Selectors" value={saleIndicatorSelector} />
                    )}
                </Box>

                {/* --- Column 2: Indicator Text & Keywords --- */}
                <Box sx={{ width: '50%' }}>
                    {editMode ? (
                        <>
                            <SelectorInput
                                label="Indicator Text Selectors"
                                values={saleIndicatorTextSelector ? saleIndicatorTextSelector.split(',').map(s => s.trim()) : []}
                                onChange={(values) => onChange('saleIndicatorTextSelector', values.join(','))}
                                helpText="Selectors for an element containing sale text (e.g., a box that says 'Special Offer')."
                                snippet="e.g. .product-alerts"
                            />
                            <Box sx={{mt: 2}}>
                                <SelectorInput
                                    label="Sale Keywords"
                                    values={Array.isArray(saleIndicatorText) ? saleIndicatorText : []}
                                    onChange={(values) => onChange('saleIndicatorText', values)}
                                    helpText="Keywords to look for inside the element above."
                                    snippet="e.g. sale, angebot"
                                />
                            </Box>
                        </>
                    ) : (
                        <>
                            <ViewField label="Indicator Text Selectors" value={saleIndicatorTextSelector} />
                            <ViewField label="Sale Keywords" value={saleIndicatorText} />
                        </>
                    )}
                </Box>
            </Box>
        </Paper>

        <Paper variant="outlined" sx={SECTION_PAPER_SX}>
            <Typography variant="h6" gutterBottom>XPath Fallbacks</Typography>

            <Box sx={{ display: 'flex', gap: 4, mt: 2 }}>
                <Box sx={{ width: '50%' }}>
                    {editMode ? (
                        <>
                            <SelectorInput
                                label="XPath Price Selectors"
                                values={xpathPriceSelector ? xpathPriceSelector.split(',').map(s => s.trim()) : []}
                                onChange={(values) => onChange('xpathPriceSelector', values.join(','))}
                                helpText="XPath expressions that point directly at the regular price node."
                                snippet="e.g., //span[@data-price]"
                            />
                            <Box sx={{ mt: 2 }}>
                                <SelectorInput
                                    label="XPath Price Attribute"
                                    values={xpathPriceAttr ? xpathPriceAttr.split(',').map(s => s.trim()) : []}
                                    onChange={(values) => onChange('xpathPriceAttr', values.join(','))}
                                    helpText="Optional attribute to read instead of inner text (e.g., data-price)."
                                    snippet="e.g., data-price"
                                />
                            </Box>
                        </>
                    ) : (
                        <>
                            <ViewField label="XPath Price Selectors" value={xpathPriceSelector} />
                            <ViewField label="XPath Price Attribute" value={xpathPriceAttr} />
                        </>
                    )}
                </Box>

                <Box sx={{ width: '50%' }}>
                    {editMode ? (
                        <>
                            <SelectorInput
                                label="XPath Sale Price Selectors"
                                values={xpathSalePriceSelector ? xpathSalePriceSelector.split(',').map(s => s.trim()) : []}
                                onChange={(values) => onChange('xpathSalePriceSelector', values.join(','))}
                                helpText="Optional XPath that directly targets the sale price."
                                snippet="e.g., //span[contains(@class,'sale')]"
                            />
                            <Box sx={{ mt: 2 }}>
                                <SelectorInput
                                    label="XPath Sale Price Attribute"
                                    values={xpathSalePriceAttr ? xpathSalePriceAttr.split(',').map(s => s.trim()) : []}
                                    onChange={(values) => onChange('xpathSalePriceAttr', values.join(','))}
                                    helpText="If your sale price lives in an attribute, list it here."
                                    snippet="e.g., data-sale-price"
                                />
                            </Box>
                        </>
                    ) : (
                        <>
                            <ViewField label="XPath Sale Price Selectors" value={xpathSalePriceSelector} />
                            <ViewField label="XPath Sale Price Attribute" value={xpathSalePriceAttr} />
                        </>
                    )}
                </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 4, mt: 3 }}>
                <Box sx={{ width: '100%' }}>
                    {editMode ? (
                        <>
                            <SelectorInput
                                label="XPath Availability Selectors"
                                values={xpathAvailabilitySelector ? xpathAvailabilitySelector.split(',').map(s => s.trim()) : []}
                                onChange={(values) => onChange('xpathAvailabilitySelector', values.join(','))}
                                helpText="XPath expressions that point to the stock indicator (used with the configured stock method)."
                                snippet="e.g., //button[@data-stock-state]"
                            />
                            <Box sx={{ mt: 2 }}>
                                <SelectorInput
                                    label="Availability Keywords"
                                    values={Array.isArray(xpathAvailabilityText) ? xpathAvailabilityText : (xpathAvailabilityText ? xpathAvailabilityText.split(',').map(s => s.trim()) : [])}
                                    onChange={(values) => onChange('xpathAvailabilityText', values)}
                                    helpText="Keywords used to determine availability when using text-based stock methods."
                                    snippet="e.g., sold out, ausverkauft"
                                />
                            </Box>
                        </>
                    ) : (
                        <>
                            <ViewField label="XPath Availability Selectors" value={xpathAvailabilitySelector} />
                            <ViewField label="Availability Keywords" value={xpathAvailabilityText} />
                        </>
                    )}
                </Box>
            </Box>
        </Paper>
      
        {showShippingRules && (
            <Paper variant="outlined" sx={SECTION_PAPER_SX}>
                <Typography variant="h6" gutterBottom>Shipping Rules</Typography>
                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    Define shipping costs based on cart value. Rules with a specific market will override the "Default" rules for that market.
                </Typography>
                
                {(!shippingRules || shippingRules.length === 0) ? (
                    <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        {editMode ? 'No rules have been added.' : 'Not configured'}
                    </Typography>
                ) : (
                    <Table size="small" sx={{ mb: 1 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Market</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Price Up To (€)</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Shipping Cost (€)</TableCell>
                                {editMode && <TableCell align="right">Actions</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                        {shippingRules.map((rule, index) => (
                            <TableRow key={index}>
                                <TableCell>
                                    {editMode ? (
                                        <MarketSelector
                                            value={toOption(rule.market_id)}
                                            onChange={(v) => {
                                                const updatedRules = [...shippingRules];
                                                // IMPORTANT: store the CANONICAL market id, not the label/code
                                                updatedRules[index].market_id = v ? (v.id || v.value || null) : null;
                                                handleShippingRulesChange(updatedRules);
                                            }}
                                            label="Market"
                                            size="small"
                                            renderInput={p => <TextField {...p} label="" placeholder="Default (All Markets)" />}
                                            sx={{ '& .MuiInputLabel-root': { display: 'none' }, '& .MuiOutlinedInput-root': { mt: 0 } }}
                                        />
                                    ) : (
                                        (rule.market_id && marketsById[rule.market_id]
                                          ? `${marketsById[rule.market_id].name} (${marketsById[rule.market_id].code})`
                                          : (rule.market_id || 'Default (All Markets)'))
                                    )}
                                </TableCell>
                                <TableCell>
                                    {editMode ? (
                                        <TextField 
                                            type="number" size="small" variant="outlined"
                                            value={rule.threshold} 
                                            onChange={e => { 
                                                const updatedRules = [...shippingRules]; 
                                                updatedRules[index].threshold = e.target.value; 
                                                handleShippingRulesChange(updatedRules); 
                                            }} 
                                            error={!!shippingErrors[index]?.threshold}
                                            helperText={shippingErrors[index]?.threshold ? "Required" : ""}
                                            sx={{ maxWidth: 120 }}
                                        />
                                    ) : ( rule.threshold )}
                                </TableCell>
                                <TableCell>
                                    {editMode ? (
                                        <TextField 
                                            type="number" size="small" variant="outlined"
                                            value={rule.cost} 
                                            onChange={e => { 
                                                const updatedRules = [...shippingRules]; 
                                                updatedRules[index].cost = e.target.value; 
                                                handleShippingRulesChange(updatedRules); 
                                            }}
                                            error={!!shippingErrors[index]?.cost}
                                            helperText={shippingErrors[index]?.cost ? "Required" : ""}
                                            sx={{ maxWidth: 120 }}
                                        />
                                    ) : ( rule.cost )}
                                </TableCell>
                                {editMode && (
                                    <TableCell align="right">
                                        <IconButton size="small"
                                            aria-label={`Remove shipping rule ${index + 1}`}
                                            onClick={() => { 
                                            const updatedRules = shippingRules.filter((_, i) => i !== index); 
                                            handleShippingRulesChange(updatedRules); 
                                        }}>
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                        </TableBody>
                    </Table>
                )}
                
                {editMode && (
                    <Button 
                        startIcon={<Add />} 
                        onClick={() => handleShippingRulesChange([...shippingRules, { market_id: null, threshold: '', cost: '' }])}
                        sx={{ mt: 1 }}
                    >
                        Add rule
                    </Button>
                )}
            </Paper>
        )}

        <Paper variant="outlined" sx={SECTION_PAPER_SX}>
            <Typography variant="h6" gutterBottom>Availability & Delivery</Typography>
            <Box sx={{ display: 'flex', gap: 4, mt: 2 }}>
                {/* --- Column 1: Availability --- */}
                <Box sx={{ width: '50%' }}>
                    {editMode ? (
                        <>
                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                <InputLabel>Stock Detection Method</InputLabel>
                                <Select value={stockMethod || 'disabled_attribute'} label="Stock Detection Method" onChange={e => onChange('stockMethod', e.target.value)}>
                                    {STOCK_METHODS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <SelectorInput
                                label="Stock Element Selectors"
                                values={stockSelector ? stockSelector.split(',').map(s => s.trim()) : []}
                                onChange={(values) => onChange('stockSelector', values.join(','))}
                                helpText="Selector for the element indicating stock status."
                                snippet="e.g. button.add-to-cart"
                            />
                            <Collapse in={stockMethod === 'text_contains' || stockMethod === 'element_in_stock'}>
                                <Box sx={{ mt: 2 }}>
                                    <SelectorInput
                                        label={stockMethod === 'element_in_stock' ? 'In-stock Keywords' : 'Out-of-stock Keywords'}
                                        values={Array.isArray(stockText) ? stockText : []}
                                        onChange={(values) => onChange('stockText', values)}
                                        helpText={stockMethod === 'element_in_stock' ? "Keywords for IN stock." : "Keywords for OUT of stock."}
                                        snippet={stockMethod === 'element_in_stock' ? "e.g. In Stock" : "e.g. Sold Out"}
                                    />
                                </Box>
                            </Collapse>
                        </>
                    ) : (
                        <>
                            <ViewField label="Stock Method" value={STOCK_METHODS.find(m => m.value === stockMethod)?.label} />
                            <ViewField label="Stock Selectors" value={stockSelector} />
                            {(stockMethod === 'text_contains' || stockMethod === 'element_in_stock') && 
                                <ViewField label="Stock Keywords" value={stockText} />
                            }
                        </>
                    )}
                </Box>
                {/* --- Column 2: Delivery & Buy Button --- */}
                <Box sx={{ width: '50%' }}>
                    {editMode ? (
                        <>
                            <SelectorInput
                                label="Buy Button Selectors"
                                values={Array.isArray(buyButtonSelectors) ? buyButtonSelectors : []}
                                onChange={(values) => onChange('buyButtonSelectors', values)}
                                helpText="Selectors for the 'Add to Cart' button."
                                snippet="e.g. button#add-to-cart"
                            />
                            {showDeliveryTimeSelector && (
                                <Box sx={{ mt: 2 }}>
                                    <SelectorInput
                                        label="Delivery Time Selectors"
                                        values={deliveryTimeSelector ? deliveryTimeSelector.split(',').map(s => s.trim()) : []}
                                        onChange={(values) => onChange('deliveryTimeSelector', values.join(','))}
                                        helpText="Selectors for the element containing estimated delivery time."
                                        snippet="e.g. .delivery-info"
                                    />
                                </Box>
                            )}
                        </>
                    ) : (
                        <>
                            <ViewField label="Buy Button Selectors" value={buyButtonSelectors} />
                            {showDeliveryTimeSelector && 
                                <ViewField label="Delivery Time Selectors" value={deliveryTimeSelector} />
                            }
                        </>
                    )}
                </Box>
            </Box>

            <Divider sx={{ my: 3 }}><Chip label="Advanced Availability Strategies" size="small" /></Divider>
            {editMode ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {(availabilityStrategies || []).map((strat, idx) => (
                  <Paper key={idx} variant="outlined" sx={{ p: 2, borderStyle: 'dashed' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2">Strategy #{idx + 1}</Typography>
                      <Button size="small" color="error" onClick={() => removeAvailabilityStrategy(idx)}>Remove</Button>
                    </Stack>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Method</InputLabel>
                          <Select
                            label="Method"
                            value={strat.method || 'text_contains'}
                            onChange={(e) => updateAvailabilityStrategy(idx, 'method', e.target.value)}
                          >
                            {STOCK_METHODS.map(m => (
                              <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                          label="Selector"
                          fullWidth
                          size="small"
                          value={strat.selector || ''}
                          onChange={(e) => updateAvailabilityStrategy(idx, 'selector', e.target.value)}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <DisabledReason
                          reason={(strat.method && !['text_contains','element_in_stock'].includes(strat.method))
                            ? 'Only the "text contains" and "element in stock" methods use this field.'
                            : null}
                        >
                          <TextField
                            label="Keywords (comma-separated)"
                            fullWidth
                            size="small"
                            value={
                              Array.isArray(strat.text || strat.keywords)
                                ? (strat.text || strat.keywords).join(', ')
                                : (strat.text || strat.keywords || '')
                            }
                            onChange={(e) => {
                              // Store raw string during typing so commas/spaces don't get eaten
                              updateAvailabilityStrategy(idx, 'text', e.target.value);
                            }}
                            onBlur={(e) => {
                              // Commit as array only when the user leaves the field
                              const vals = e.target.value.split(',').map(v => v.trim()).filter(Boolean);
                              updateAvailabilityStrategy(idx, 'text', vals);
                            }}
                            disabled={strat.method && !['text_contains','element_in_stock'].includes(strat.method)}
                          />
                        </DisabledReason>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
                <Button variant="outlined" size="small" onClick={addAvailabilityStrategy}>
                  Add availability strategy
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {(availabilityStrategies || []).length === 0 && (
                  <Typography variant="body2" color="text.secondary">No advanced strategies configured.</Typography>
                )}
                {(availabilityStrategies || []).map((strat, idx) => (
                  <ViewField
                    key={idx}
                    label={`Strategy #${idx + 1}`}
                    value={`${strat.method || ''} | ${strat.selector || ''} | ${(Array.isArray(strat.text || strat.keywords) ? (strat.text || strat.keywords).join(', ') : (strat.text || strat.keywords || ''))}`}
                  />
                ))}
              </Box>
            )}
        </Paper>
    </Box>
  );
}
